import { useCallback, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { useNavigate, useRouterState } from "@tanstack/react-router";

import { DetailsContent, DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import { useBuyerFromRoute } from "components/buyer_details/BuyerDetailLayout";
import BuyerHeader from "components/buyer_details/BuyerHeader";
import ContactsPanel from "components/buyer_details/contacts_panel/ContactsPanel";
import CustomBriefingTab from "components/buyer_details/CustomBriefingTab";
import { getTabConfigFromLocation, labelledRoutes } from "components/buyer_details/routingUtils";
import SummaryTab from "components/buyer_details/SummaryTab";
import { SupplierTab } from "components/buyer_details/SupplierTab";
import { PrimaryTab, SecondaryTab } from "components/buyer_details/types";
import UncollectedDocuments, {
  FilterableDocuments,
} from "components/documents/FilterableDocuments";
import {
  convertDocumentFiltersToSearchRequest,
  createDocumentColumns,
  DEFAULT_DOCUMENT_FILTERS,
  isIncompleteCoverageBuyer,
} from "components/documents/utils";
import { FilterableFrameworks } from "components/framework/FilterableFrameworks";
import {
  DEFAULT_FILTERS as DEFAULT_FRAMEWORK_FILTERS,
  FrameworkColumns,
} from "components/framework/utils";
import { FilterableNoticeTable } from "components/notices/FilterableNoticeTable";
import { useFilterableNoticeTableState } from "components/notices/useFilterableNoticeTableState";
import { EMPTY_FILTERS, NoticeColumns, NoticeFilters } from "components/notices/utils";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import Tabs from "components/ui/tabs/Tabs";
import { AI_REPORT_CONFIG, useVariableValue } from "lib/featureFlags";
import { useDocuments } from "lib/hooks/api/documents/useDocuments";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import { useConfirmUserDataSubscription } from "lib/providers/Paywall";
import { SortState } from "lib/search/types";
import * as tracking from "lib/tracking";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { LoggedEntityType, NoticesStagesv2 } from "lib/types/graphQLEnums";
import { ColumnSetting } from "lib/types/models";
import { stripPathPrefix } from "lib/utils";

import css from "./BuyerDetailsPage.module.scss";

const FRAMEWORK_COLUMNS: ColumnSetting<FrameworkColumns>[] = [
  "signalScore",
  "title",
  "stage",
  "value",
  "suppliers",
  "closeDate",
].map((c) => {
  return {
    field: c,
    visible: true,
  } as ColumnSetting<FrameworkColumns>;
});

export function BuyerDetailsPage() {
  const buyer = useBuyerFromRoute();
  // Paywall modal which prevents users who aren't subscribed from viewing buyer profile
  useConfirmUserDataSubscription("BUYERS", "BUYERS");
  useLogEntityViewOnOpen(LoggedEntityType.Buyer, buyer?.guid);

  // Page view tracking (replaces withAppLayout's dynamic pageViewProps)
  useEffect(() => {
    if (!buyer) return;
    document.title = `${buyer.name} | Stotles`;
    tracking.pageView("Buyer Profile", {
      "Buyer name": buyer.name,
      "Buyer ID": buyer.id,
    });
  }, [buyer]);

  if (!buyer) return null;

  return <BuyerDetailsContent buyer={buyer} />;
}

function BuyerDetailsContent({
  buyer,
}: {
  buyer: NonNullable<ReturnType<typeof useBuyerFromRoute>>;
}) {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();
  const subscription = useSubscription();
  const showFrameworksPaywall = !subscription.hasDataTypes("FRAMEWORKS");
  const showDocumentsPaywallBanner = !subscription.hasDataTypes("DOCUMENTS");
  const showContactsPaywall = !subscription.hasDataTypes("CONTACTS");

  const aiReportConfigEnabled = useVariableValue(AI_REPORT_CONFIG, false);

  const navigate = useNavigate();
  // Reactive pathname so the view re-renders when navigating to sibling routes
  // (e.g. /supplier/$guid). The full pathname also covers named child routes,
  // which the catch-all _splat param does not.
  const routerPathname = useRouterState({ select: (s) => s.location.pathname });
  const buyerPrefix = `/buyers/${buyer.guid}`;
  const location = stripPathPrefix(routerPathname, buyerPrefix, "/");

  const { setNoticeTableFilters, setNoticeTableColumns } = useFilterableNoticeTableState();
  const setLocation = useCallback(
    (route: string) => {
      void navigate({
        to: `/buyers/${buyer.guid}${route}`,
        search: (prev: Record<string, unknown>) => prev,
      });
    },
    [navigate, buyer.guid],
  );

  const tabConfig = useMemo(() => getTabConfigFromLocation(location), [location]);

  const activeTabLabel = useMemo(() => {
    const conf = getTabConfigFromLocation(location);
    const activeRoute = conf.secondary || conf.primary;
    return labelledRoutes[activeRoute];
  }, [location]);

  const headerClicked = ({ trackingType, route }: { trackingType: string; route: string }) => {
    logEvent(EventNames.highlightClicked, {
      "Context source": "Header card",
      "Highlight type": trackingType,
    });
    setNoticeTableFilters(undefined);
    setNoticeTableColumns(undefined);
    setLocation(route);
  };

  const tabClicked = useCallback(
    (loc: PrimaryTab | SecondaryTab) => {
      setLocation(loc);
      const config = getTabConfigFromLocation(loc);
      const activeRoute = config.secondary || config.primary;
      logEvent(EventNames.tabChanged, {
        "Context source": config.secondary ? "Secondary Tab" : "Primary Tab",
        "Tab Selected": labelledRoutes[activeRoute],
      });
    },
    [setLocation, logEvent],
  );

  const { data: buyerDocumentsCoverage } = useDocuments(
    convertDocumentFiltersToSearchRequest(
      { ...DEFAULT_DOCUMENT_FILTERS, organisationIds: [buyer.guid] },
      currentUser,
      { pageSize: 10, current: 1 },
    ),
    { enabled: tabConfig.primary === PrimaryTab.DOCUMENTS_NEW },
  );

  const showDocumentsBanner = isIncompleteCoverageBuyer(buyer.categories);

  const displayedBuyerDetails: JSX.Element = useMemo(() => {
    const baseFilter: NoticeFilters = {
      ...EMPTY_FILTERS,
      buyers: [buyer.guid],
      includeHiddenNotices: true,
    };
    const allMatchingSignalsFilter: NoticeFilters = {
      ...baseFilter,
      signals: ["Keywords", "Partners", "Competitors", "CPV codes"],
      relevanceScore: ["Low"],
    };
    const allMatchingExpiriesFilter: NoticeFilters = {
      ...baseFilter,
      signals: ["Keywords", "Partners", "Competitors", "CPV codes"],
      relevanceScore: ["Low"],
      expiryDate: {
        filter: {
          relativeFrom: "PT0S",
        },
        hideNulls: true,
      },
      closeDate: {
        hideNulls: false,
      },
      stage: [
        NoticesStagesv2.AwardedContract,
        NoticesStagesv2.ExpiredContract,
        NoticesStagesv2.CancelledAward,
      ],
      sort: { field: "expiry_date", order: "DESC" } as SortState,
    };
    const allMatchingOpenOpportunitiesFilter: NoticeFilters = {
      ...baseFilter,
      signals: ["Keywords", "Partners", "Competitors", "CPV codes"],
      relevanceScore: ["Low"],
      stage: [NoticesStagesv2.Pipeline, NoticesStagesv2.PreTenderV2, NoticesStagesv2.OpenTender],
    };

    const currentSelectedTab = (): {
      filter: NoticeFilters;
      columns: NoticeColumns[];
      paywall: boolean;
    } => {
      switch (activeTabLabel) {
        case "Activity": {
          return {
            filter: baseFilter,
            columns: [
              "relevance_score",
              "title",
              "published_date",
              "expiry_date",
              "value",
              "stage",
            ],
            paywall: false,
          };
        }
        case "Activity: All activity": {
          return {
            filter: allMatchingSignalsFilter,
            columns: [
              "relevance_score",
              "title",
              "published_date",
              "expiry_date",
              "value",
              "stage",
            ],
            paywall: false,
          };
        }
        case "Activity: Upcoming contract expiries": {
          return {
            filter: allMatchingExpiriesFilter,
            columns: ["relevance_score", "title", "expiry_date", "value", "stage", "suppliers"],
            paywall: true,
          };
        }
        case "Activity: Open opportunities": {
          return {
            filter: allMatchingOpenOpportunitiesFilter,
            columns: [
              "relevance_score",
              "title",
              "published_date",
              "expiry_date",
              "value",
              "stage",
            ],
            paywall: false,
          };
        }
        default: {
          return {
            filter: baseFilter,
            columns: [
              "relevance_score",
              "title",
              "published_date",
              "expiry_date",
              "value",
              "stage",
            ],
            paywall: false,
          };
        }
      }
    };

    switch (tabConfig.primary) {
      case PrimaryTab.CONTACTS:
        return (
          <ContactsPanel
            buyer={buyer}
            handleTabNavigation={tabClicked}
            activeTab={tabConfig.secondary}
          />
        );

      case PrimaryTab.PROCUREMENT_ACTIVITY:
        return (
          <FilterableNoticeTable
            defaultFilters={currentSelectedTab().filter}
            defaultColumns={currentSelectedTab().columns}
            hiddenSections={["buyers"]}
            paywallEnabled={currentSelectedTab().paywall}
          />
        );

      case PrimaryTab.FRAMEWORKS:
        return !showFrameworksPaywall ? (
          <FilterableFrameworks
            defaultColumns={FRAMEWORK_COLUMNS}
            defaultFilters={{ ...DEFAULT_FRAMEWORK_FILTERS, buyerIds: [buyer.guid] }}
            hiddenFilterSections={["buyers"]}
          />
        ) : (
          <></>
        );

      case PrimaryTab.SUPPLIERS:
        return <SupplierTab buyer={buyer} />;

      case PrimaryTab.CUSTOM_BRIEFING:
        return <CustomBriefingTab buyer={buyer} />;

      case PrimaryTab.DOCUMENTS_NEW:
        return (
          <TrackingProvider data={{ "Context source": "Document tab" }}>
            {showDocumentsPaywallBanner && (
              <PaywallBanner featureType="DOCUMENTS" className={css.paywallBanner} />
            )}
            <FilterableDocuments
              defaultColumns={createDocumentColumns({ excludedColumns: ["buyers"] })}
              defaultFilters={{ ...DEFAULT_DOCUMENT_FILTERS, organisationIds: [buyer.guid] }}
              hiddenFilters={["organisationIds"]}
              hiddenSections={["buyers"]}
              emptyState={
                buyerDocumentsCoverage?.totalResults === 0 ? <UncollectedDocuments /> : undefined
              }
              showBanner={showDocumentsBanner}
            />
          </TrackingProvider>
        );

      default:
        return (
          <SummaryTab
            buyerGuid={buyer.guid}
            showMatchingExpiries={() => {
              tabClicked(SecondaryTab.UPCOMING_EXPIRIES);
            }}
          />
        );
    }
  }, [
    buyer,
    tabConfig.primary,
    tabConfig.secondary,
    activeTabLabel,
    tabClicked,
    showFrameworksPaywall,
    showDocumentsPaywallBanner,
    buyerDocumentsCoverage?.totalResults,
    showDocumentsBanner,
  ]);

  return (
    <DetailsPage>
      <DetailsHeader className={css.header}>
        <TrackingProvider data={{ "Context source": "Header" }}>
          <BuyerHeader buyer={buyer} onHeaderChange={headerClicked} currentTabPath={location} />
        </TrackingProvider>

        {currentUser?.isAdmin && (
          <a
            href={`https://tools.stotles.com/organisations/${buyer.guid}`}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, position: "absolute", top: 4, right: 16 }}
          >
            Edit in admin tools
          </a>
        )}

        <Tabs
          value={tabConfig.primary}
          onChange={(key) => tabClicked(key as PrimaryTab)}
          margin="24px 0 0 0"
          height="unset"
          items={(
            [
              {
                key: PrimaryTab.SUMMARY,
                label: "Summary",
              },
              {
                key: PrimaryTab.CONTACTS,
                label: "Contacts",
                showPaywall: showContactsPaywall,
              },
              {
                key: PrimaryTab.PROCUREMENT_ACTIVITY,
                label: "Notices",
              },
              {
                key: PrimaryTab.FRAMEWORKS,
                label: "Frameworks",
                showPaywall: showFrameworksPaywall,
              },
              {
                key: PrimaryTab.DOCUMENTS_NEW,
                label: "Documents",
                showPaywall: showDocumentsPaywallBanner,
              },
              {
                key: PrimaryTab.SUPPLIERS,
                label: "Suppliers",
              },
              {
                key: PrimaryTab.CUSTOM_BRIEFING,
                label: "Custom Briefing",
                icon: "magicWand",
              },
            ] as const
          )
            .filter((item) =>
              item.key === PrimaryTab.CUSTOM_BRIEFING ? aiReportConfigEnabled : true,
            )
            // Children are rendered below since we want the tabs to be in a sticky header and therefore the children cannot be nested within unfortunately
            .map((item) => ({
              ...item,
              children: null,
            }))}
        />
      </DetailsHeader>

      {/* Summary and documents content is surrounded by a white background container
       from the outset, whereas the others are not */}
      {tabConfig.primary === PrimaryTab.SUMMARY ? (
        <DetailsContentCard>
          <TrackingProvider data={{ "Context source": activeTabLabel }}>
            {displayedBuyerDetails}
          </TrackingProvider>
        </DetailsContentCard>
      ) : (
        <DetailsContentWrapper isDocumentsTab={tabConfig.primary === PrimaryTab.DOCUMENTS_NEW}>
          <TrackingProvider data={{ "Context source": activeTabLabel }}>
            {displayedBuyerDetails}
          </TrackingProvider>
        </DetailsContentWrapper>
      )}
    </DetailsPage>
  );
}

const DetailsContentWrapper = styled(DetailsContent)<{
  isDocumentsTab: boolean;
}>(({ isDocumentsTab }) => ({
  padding: isDocumentsTab ? 0 : "16px 24px",
}));

const DetailsContentCard = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  margin: "16px 24px",
  padding: "16px",
}));
